import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import sys from '../../system';

import { accounts, tools } from '@truckdown/jsclient/apis';

import { IAntiforgery } from '../../services/IAntiforgery';

import {
    solid, regular
} from '@truckdown/jsclient/icons';

sys.fontLibrary.add(solid.faTasks);
sys.fontLibrary.add(solid.faTable);
sys.fontLibrary.add(solid.faChartLine);
sys.fontLibrary.add(solid.faDatabase);
sys.fontLibrary.add(solid.faAddressBook);
sys.fontLibrary.add(solid.faHashtag);
sys.fontLibrary.add(solid.faSignInAlt);
sys.fontLibrary.add(solid.faTrashCan);
sys.fontLibrary.add(solid.faSpinner);
sys.fontLibrary.add(solid.faBookmark);
sys.fontLibrary.add(regular.faBookmark);
sys.fontLibrary.add(solid.faClipboardUser);
sys.fontLibrary.add(solid.faEdit);
sys.fontLibrary.add(solid.faBuilding);
sys.fontLibrary.add(solid.faForward);
sys.fontLibrary.add(solid.faBackward);
sys.fontLibrary.add(solid.faUsers);
sys.fontLibrary.add(solid.faLocationDot);
sys.fontLibrary.add(solid.faCirclePause);
sys.fontLibrary.add(solid.faCircleExclamation);
sys.fontLibrary.add(solid.faCircleMinus);
sys.fontLibrary.add(solid.faCircleRight);

interface ISearchResult extends tools.IRelationshipResult {
    accountData?: tools.IRelationshipResult;
}

interface IListResult {
    type: tools.RelationshipType,
    text: string,
    active: boolean,
    total: number,
    results: ISearchResult[],
    lastIds: string[],
    previous: (ev: Event) => void,
    next: (ev: Event) => void
}

interface ISelectItem {
    value: string,
    display: string
}

interface IHelperData {
    regions?: tools.Region[],
    services?: tools.Service[],
    csrs?: accounts.ContactUserInfoModel[]
}

export default defineComponent({
    name: 'control-panel',
    props: {
        searchText: String
    },
    setup: function (props, context) {
        //display

        const showResults = ref<boolean>(false);

        function toggleView(ev: Event): void {
            showResults.value = !showResults.value;
        }

        //search helper

        const showHelper = ref<boolean>(false);
        const selectedParam = ref<string>('keywords');
        const paramValue = ref<string>('');
        const isParamList = ref<boolean>(false);
        const paramItems = ref<ISelectItem[]>([]);

        const isOr = ref<boolean>(false);
        const isNegative = ref<boolean>(false);

        var toggleHelper = function (ev: Event) {
            showHelper.value = !showHelper.value;
        }

        var clearSettings = function () {
            selectedParam.value = 'keywords';
            paramValue.value = '';
            isParamList.value = false;
            paramItems.value = [];
            isOr.value = false;
            isNegative.value = false;
        }

        var setDefaultValue = function () {
            if (isParamList.value && paramItems.value.length > 0) {
                paramValue.value = paramItems.value[0].value;
            } else {
                paramValue.value = '';
            }
        }

        var clearHelper = function (ev: Event) {
            adminText.value = '';
            clearSettings();
        }

        var addHelper = function (ev: Event) {
            if (paramValue.value && paramValue.value.trim().length > 0) {
                var term = (adminText.value + '').trim() + ' ';

                if (isOr.value) {
                    term = term + '||';
                }
                if (isNegative.value) {
                    term = term + '!';
                }
                adminText.value = (term + paramValue.value).trim();
            }
            setDefaultValue();
        }

        var setOr = function (ev: Event) {
            isOr.value = !isOr.value;
        }

        var setNegative = function (ev: Event) {
            isNegative.value = !isNegative.value;
        }

        var orClass = computed(() => {
            return isOr.value ? 'primary' : '';
        });

        var negativeClass = computed(() => {
            return isNegative.value ? 'primary' : '';
        });

        var helperData: IHelperData = {};

        async function loadRegions() {
            isParamList.value = true;
            paramItems.value = [];
            paramValue.value = '';

            if (!helperData.regions) {
                var mgr = sys.framework.getRequiredService<tools.ISharedEntitiesManager>('ISharedEntitiesManager');
                helperData.regions = await mgr.allRegions();
            }

            if (helperData.regions) {
                paramItems.value = helperData.regions.map<ISelectItem>(i => {
                    return {
                        value: '#' + i.code,
                        display: i.name
                    };
                });
            }

            setDefaultValue();
        };

        async function loadServices() {
            isParamList.value = true;
            paramItems.value = [];
            paramValue.value = '';

            if (!helperData.services) {
                var mgr = sys.framework.getRequiredService<tools.ISharedEntitiesManager>('ISharedEntitiesManager');
                helperData.services = await mgr.allServices();
            }

            if (helperData.services) {
                paramItems.value = helperData.services
                    .filter(i => i.type == tools.ServiceType.Service)
                    .map<ISelectItem>(i => {
                        return {
                            value: '#' + i.code,
                            display: i.name
                        };
                    });
            }

            setDefaultValue();
        };

        async function loadNetworks() {
            isParamList.value = true;
            paramItems.value = [];
            paramValue.value = '';

            if (!helperData.services) {
                var mgr = sys.framework.getRequiredService<tools.ISharedEntitiesManager>('ISharedEntitiesManager');
                helperData.services = await mgr.allServices();
            }

            if (helperData.services) {
                paramItems.value = helperData.services
                    .filter(i => i.type == tools.ServiceType.DealerNetwork)
                    .map<ISelectItem>(i => {
                        return {
                            value: '#' + i.code,
                            display: i.name
                        };
                    });
            }

            setDefaultValue();
        };

        async function loadCsrs() {
            isParamList.value = true;
            paramItems.value = [];
            paramValue.value = '';

            if (!helperData.csrs) {
                var usersQuery = sys.framework.getRequiredService<accounts.IUsersQuery>('IUsersQuery');
                helperData.csrs = await usersQuery.getCsrs();
            }

            if (helperData.csrs) {
                paramItems.value = helperData.csrs.map<ISelectItem>(i => {
                    return {
                        value: '#' + (i.username ?? i.email),
                        display: i.firstName + ' ' + i.lastName
                    };
                });
            }

            setDefaultValue();
        };

        function loadAccountTypes() {
            isParamList.value = true;
            paramItems.value = [
                { value: '#vendor', display: 'Vendor' },
                { value: '#fleet', display: 'Fleet' },
                { value: '#developer', display: 'Developer' },
                { value: '#callcenter', display: 'Call Center' }
            ];
            paramValue.value = '#vendor';
        };

        function loadAccountStatuses() {
            isParamList.value = true;
            paramItems.value = [
                { value: '#active', display: 'Active' },
                { value: '#inactive', display: 'Inactive' },
                { value: '#dna', display: 'DNA' },
                { value: '#purge', display: 'Purged' },
                { value: '#active ||#inactive ||#dna ||#purge', display: 'All' }
            ];
            paramValue.value = '#active';
        };

        function loadProducts() {
            isParamList.value = true;
            paramItems.value = [
                { value: '#loc-paid', display: 'Any Paid Location' },
                { value: '#truckdown_stock', display: 'TruckDown Stock' },
                { value: '#truckdown_branded', display: 'TruckDown Branded' },
                { value: '#truckdown_pro', display: 'TruckDown Pro' },
                { value: '#truckdown_elite', display: 'TruckDown Elite' },
                { value: '#planrv_stock', display: 'Plan RV Stock' },
                { value: '#planrv_branded', display: 'Plan RV Branded' },
                { value: '#planrv_pro', display: 'Plan RV Pro' },
                { value: '#planrv_elite', display: 'Plan RV Elite' },
                { value: '#basic', display: 'Free Fleet' },
                { value: '#ace', display: 'Ace Fleet' },
                { value: '#basicrouting', display: 'Free Routing' },
                { value: '#route50', display: 'Route 50 Fleet' },
                { value: '#route200', display: 'Route 200 Fleet' }
            ];
            paramValue.value = '#loc-paid';
        };

        function loadSites() {
            isParamList.value = true;
            paramItems.value = [
                { value: '#truckdown', display: 'TruckDown' },
                { value: '#planrv', display: 'Plan RV' }
            ];
            paramValue.value = '#truckdown';
        };

        function loadMisc() {
            isParamList.value = true;
            paramItems.value = [
                { value: '#news', display: 'Receive Newsletter' }
            ];
            paramValue.value = '#news';
        };

        function loadKeywords() {
            paramValue.value = '';
            isParamList.value = false;
            paramItems.value = [];
        };

        watch(selectedParam, (newSelectedParam, oldSelectedParam) => {
            switch (newSelectedParam) {
                case 'region':
                    loadRegions();
                    break;

                case 'service':
                    loadServices();
                    break;

                case 'network':
                    loadNetworks();
                    break;

                case 'type':
                    loadAccountTypes();
                    break;

                case 'status':
                    loadAccountStatuses();
                    break;

                case 'product':
                    loadProducts();
                    break;

                case 'csr':
                    loadCsrs();
                    break;

                case 'site':
                    loadSites();
                    break;

                case 'misc':
                    loadMisc();
                    break;

                case 'accountid':
                case 'keywords':
                default:
                    loadKeywords();
                    break;
            }
        });

        //search

        const adminSelected = ref<boolean>(false);

        const adminText = ref<string | undefined>(props.searchText);
        const searchError = ref<string | undefined>();

        const searchView = ref<string>('accounts_selected');

        var showAccounts = function (ev: Event) {
            searchView.value = 'accounts_selected';
        };

        var showLocations = function (ev: Event) {
            searchView.value = 'locations_selected';
        };

        var showUsers = function (ev: Event) {
            searchView.value = 'users_selected';
        };

        watch(adminText, (newAdminText, oldAdminText) => {
            context.emit('update', newAdminText);
        });
        watch(() => props.searchText, (newAdminText, oldAdminText) => {
            adminText.value = newAdminText;
        });

        const accounts = ref<IListResult>({
            type: tools.RelationshipType.Account,
            active: false,
            text: '',
            total: 0,
            results: [],
            lastIds: [],
            previous: function (ev: Event) { previous(this); },
            next: function (ev: Event) { next(this); }
        });
        const locations = ref<IListResult>({
            type: tools.RelationshipType.Location,
            active: false,
            text: '',
            total: 0,
            results: [],
            lastIds: [],
            previous: function (ev: Event) { previous(this); },
            next: function (ev: Event) { next(this); }
        });
        const users = ref<IListResult>({
            type: tools.RelationshipType.User,
            active: false,
            text: '',
            total: 0,
            results: [],
            lastIds: [],
            previous: function (ev: Event) { previous(this); },
            next: function (ev: Event) { next(this); }
        });

        var getStartCount = function (list: IListResult): number {
            return list.lastIds.length * 15 + 1;
        }

        var getEndCount = function (list: IListResult): number {
            return (list.lastIds.length * 15) + list.results.length;
        }

        var showPrevious = function (list: IListResult): boolean {
            return list.lastIds.length > 0;
        }

        var showNext = function (list: IListResult): boolean {
            return list.total > ((list.lastIds.length + 1) * 15);
        }

        var previous = async function (list: IListResult) {
            await searchItems(list, list.text, -1);
        }

        var next = async function (list: IListResult) {
            await searchItems(list, list.text, 1);
        }

        var getSearchQuery = function (list: IListResult, direction: number): tools.SearchRelationshipRequest {
            var req = new tools.SearchRelationshipRequest();
            req.tags = list.text;
            req.type = list.type;
            req.limit = 15;
            if (direction > 0) {
                //next
                if (list.results.length > 0) {
                    req.lastId = list.results[list.results.length - 1]._id;
                    list.lastIds.push(req.lastId);
                }

            } else if (direction < 0) {
                //previous
                if (list.lastIds.length > 0) {
                    list.lastIds.pop();
                }

                if (list.lastIds.length > 0) {
                    req.lastId = list.lastIds[list.lastIds.length - 1];
                }

            } else {
                list.lastIds = [];
            }

            return req;
        }

        var populateAccountData = async function (mgr: tools.IRelationshipService, results: ISearchResult[]) {
            let req = new tools.GetRelationshipsRequest();

            req.relationships = results.map(i => {
                let r = new tools.GetRelationshipRequest();
                r.type = tools.RelationshipType.Account;
                r._id = i.aid;
                return r;
            });

            let res = await mgr.getRelationships(req);
            for (var i = 0; i < results.length; i++) {
                let matches = res.filter(a => a._id == results[i].aid);
                if (matches.length > 0) {
                    results[i].accountData = matches[0];
                }
            }
        }

        var searchItems = async function (list: IListResult, searchText: string, direction: number): Promise<void>  {
            if (!list.active) {
                list.active = true;
                try {
                    list.text = searchText;
                    let mgr = sys.framework.getRequiredService<tools.IRelationshipService>('IRelationshipService');
                    var qry = getSearchQuery(list, direction);

                    let res = await mgr.search(qry);

                    list.total = res.cnt;
                    list.results = res.res as ISearchResult[];

                    if (list.type !== tools.RelationshipType.Account) {
                        await populateAccountData(mgr, list.results);
                    }
                } finally {
                    list.active = false;
                }
            }
        }

        var search = async function (ev: Event): Promise<void> {
            showResults.value = true;
            searchError.value = undefined;
            if (adminText.value && adminText.value.length > 0) {
                await Promise.all([
                    searchItems(accounts.value, adminText.value, 0),
                    searchItems(locations.value, adminText.value, 0),
                    searchItems(users.value, adminText.value, 0)
                ]);
            } else {
                searchError.value = 'Enter search criteria';
            }
        };

        const searchClass = computed(() => {
            if (searchError.value && searchError.value.length > 0) {
                return 'error';
            }
            return '';
        })

        const searchPlaceholder = computed(() => {
            if (searchError.value && searchError.value.length > 0) {
                return searchError.value;
            }
            return 'Admin Search';
        })

        function focusAdminInput(ev: Event) {
            adminSelected.value = true;
        };

        function blurAdminInput(ev: Event) {
            adminSelected.value = false;
            if (searchError.value && searchError.value.length > 0) {
                searchError.value = undefined;
            }
        };

        //login as

        var antiForgery = sys.framework.getRequiredService<IAntiforgery>('IAntiforgery');

        const loginAsText = ref<string>('');
        const loginAsError = ref<string | undefined>();

        const afField = ref<string>(antiForgery.formFieldName);
        const afValue = ref<string>(antiForgery.token);

        const loginAsSelected = ref<boolean>(false);

        function focusLoginAsInput(ev: Event) {
            loginAsSelected.value = true;
        };

        function blurLoginAsInput(ev: Event) {
            loginAsSelected.value = false;
            if (loginAsText.value && loginAsText.value.length > 0) {
                loginAsError.value = undefined;
            }
        };

        function submitLoginForm(ev: Event) {
            if (loginAsText.value && loginAsText.value.length > 0) {

                loginAsError.value = undefined;
                return true;
            }

            loginAsError.value = 'Login As is Required';
            ev.preventDefault();
            return false;
        }

        const loginAsClass = computed(() => {
            if (loginAsError.value && loginAsError.value.length > 0) {
                return 'error';
            }
            return '';
        })

        const loginAsPlaceholder = computed(() => {
            if (loginAsError.value && loginAsError.value.length > 0) {
                return loginAsError.value;
            }
            return 'Login As';
        })

        //history

        const bookmarked = ref<accounts.AccessItemModel[]>([]);
        const recent = ref<accounts.AccessItemModel[]>([]);
        const isHistoryLoaded = ref<boolean>(false);
        const historyView = ref<string>('bookmarked_selected');

        async function addBookmark(id: string): Promise<void> {
            let mgr = sys.framework.getRequiredService<accounts.IAccessItemsManager>('IAccessItemsManager');
            let res = await mgr.addBookmark(id);
            if (res && res !== null && res.isPinned) {
                let idx = recent.value.map(i => i.accountId).indexOf(id);
                if (idx >= 0) {
                    recent.value.splice(idx, 1);
                }
                bookmarked.value.push(res);
                bookmarked.value = bookmarked.value.sort((a: accounts.AccessItemModel, b: accounts.AccessItemModel) => {
                    let idxA = a.name.indexOf(' - ');
                    let numA = parseInt(a.name.substring(0, idxA));

                    let idxB = b.name.indexOf(' - ');
                    let numB = parseInt(b.name.substring(0, idxB));

                    if (numA > numB) {
                        return 1;
                    } else if (numA < numB) {
                        return -1;
                    } else {
                        return 0;
                    }
                });
            }
        }

        async function removeBookmark(id: string): Promise<void> {
            let mgr = sys.framework.getRequiredService<accounts.IAccessItemsManager>('IAccessItemsManager');

            let res = await mgr.removeBookmark(id);
            if (res && res !== null) {
                //remove from bookmarked

                let idx = bookmarked.value.map(i => i.accountId).indexOf(id);
                if (idx >= 0) {
                    bookmarked.value.splice(idx, 1);
                }

                //add to recent
                recent.value.splice(0, 0, res);
            }
        }

        function showRecent(ev: Event) {
            historyView.value = 'recent_selected';
        };

        function showBookmarked(ev: Event) {
            historyView.value = 'bookmarked_selected';
        };

        onMounted(async () => {
            let mgr = sys.framework.getRequiredService<accounts.IAccessItemsManager>('IAccessItemsManager');

            let results = await mgr.get();

            bookmarked.value = results.filter((itm) => itm.isPinned).sort((a: accounts.AccessItemModel, b: accounts.AccessItemModel) => {
                let idxA = a.name.indexOf(' - ');
                let numA = parseInt(a.name.substring(0, idxA));

                let idxB = b.name.indexOf(' - ');
                let numB = parseInt(b.name.substring(0, idxB));

                if (numA > numB) {
                    return 1;
                } else if (numA < numB) {
                    return -1;
                } else {
                    return 0;
                }
            });

            recent.value = results.filter((itm) => !itm.isPinned);

            isHistoryLoaded.value = true;
        });

        //misc

        function close(ev: Event) {
            context.emit('close');
        }

        function none(evt: Event) {

        };

        function getViewAccountUrl(id: string): string {
            return '/accounts-admin/view-account/' + encodeURIComponent(id);
        }

        //return

        return {
            //display

            showResults,
            toggleView,

            //search helper

            showHelper,
            toggleHelper,
            selectedParam,
            paramValue,
            isParamList,
            paramItems,
            isOr,
            isNegative,
            clearHelper,
            addHelper,
            setOr,
            setNegative,
            orClass,
            negativeClass,

            //search

            adminSelected,
            focusAdminInput,
            blurAdminInput,
            adminText,
            search,
            accounts,
            locations,
            users,
            showPrevious,
            showNext,
            searchPlaceholder,
            searchClass,
            getStartCount,
            getEndCount,
            searchView,
            showAccounts,
            showLocations,
            showUsers,

            //loginas

            afField,
            afValue,
            loginAsSelected,
            focusLoginAsInput,
            blurLoginAsInput,
            loginAsText,
            loginAsError,
            submitLoginForm,
            loginAsPlaceholder,
            loginAsClass,

            //history

            bookmarked,
            recent,
            isHistoryLoaded,
            historyView,
            addBookmark,
            removeBookmark,
            showRecent,
            showBookmarked,

            //misc

            close,
            none,
            getViewAccountUrl
        };
    }
});


